import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  getReport,
  saveProfileSelection,
} from "~/lib/server-fns";
import { fetchAllProfiles } from "~/lib/fetch-profiles";
import { PrintView } from "~/components/report/PrintView";
import { ProfileHeader } from "~/components/report/ProfileHeader";
import { ProfileContent } from "~/components/report/ProfileContent";
import { ProfileInvitations } from "~/components/report/ProfileInvitations";
import { ProfileTabs } from "~/components/report/ProfileTabs";
import { KeyModelContent } from "~/components/report/KeyModelContent";
import { RichText } from "~/components/RichText";
import { ShareLink } from "~/components/report/ShareLink";
import { RelatedProfilesSidebar } from "~/components/report/RelatedProfilesSidebar";
import {
  getMatchingProfile,
  getMatchingProfiles,
  SECTION_LABELS,
  profiles as fallbackProfiles,
} from "~/data/profiles";
import { captureHandledException } from "~/lib/handled-errors";

export const Route = createFileRoute("/report/$id")({
  component: ReportPage,
  validateSearch: (search: Record<string, unknown>) => ({
    profile: (search.profile as string) || undefined,
    force_dual:
      search.force_dual === "true" || search.force_dual === true || undefined,
  }),
  loader: async ({ params }) => {
    async function loadSanityProfiles() {
      try {
        return await fetchAllProfiles();
      } catch (err) {
        await captureHandledException(err, {
          feature: "report",
          action: "load-sanity-profiles",
          layer: "loader",
          fallback: "static-profiles",
          extra: { route: "report", reportId: params.id },
        });
        return null;
      }
    }

    const [reportData, sanityProfiles] = await Promise.all([
      getReport({ data: params.id }),
      loadSanityProfiles(),
    ]);
    return { ...reportData, sanityProfiles };
  },
  errorComponent: () => (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-3xl text-core-orange mb-4">Report Not Found</h1>
        <p className="text-white/60 mb-6">
          This report link may be invalid or expired.
        </p>
        <a
          href="/"
          className="inline-block bg-core-orange text-white font-bold py-2 px-6 rounded-full hover:opacity-90 transition-opacity"
        >
          Take the Assessment
        </a>
      </div>
    </div>
  ),
});

function ReportPage() {
  const data = Route.useLoaderData();
  const { profile: profileParam, force_dual: forceDual } = Route.useSearch();
  const navigate = Route.useNavigate();
  const router = useRouter();
  const [optimisticProfileId, setOptimisticProfileId] = useState<string | null>(
    null,
  );
  const [inFlight, setInFlight] = useState(0);

  useEffect(() => {
    if (
      inFlight === 0 &&
      optimisticProfileId &&
      data.selectedProfile === optimisticProfileId
    ) {
      setOptimisticProfileId(null);
    }
  }, [data.selectedProfile, optimisticProfileId, inFlight]);

  const allProfiles = data.sanityProfiles?.length
    ? data.sanityProfiles
    : fallbackProfiles;

  const scores = {
    identity: data.identityScore,
    purpose: data.purposeScore,
    community: data.communityScore,
  };

  const matchResult = getMatchingProfiles(scores, forceDual, allProfiles);

  // Optimistic local selection wins until the save resolves and the loader
  // refetches. Otherwise fall back to URL param, then server-persisted value.
  const effectiveProfileParam =
    optimisticProfileId || profileParam || data.selectedProfile || undefined;

  // If dual profile and no selection (URL or persisted), redirect to selector
  if (matchResult.isDualProfile && !effectiveProfileParam) {
    navigate({
      to: "/report/select/$id",
      params: { id: data.id },
      replace: true,
    });
    return null;
  }

  // Resolve the profile to display
  const selectedProfile = effectiveProfileParam
    ? (allProfiles.find((p) => p.id === effectiveProfileParam) ??
      getMatchingProfile(scores, allProfiles))
    : getMatchingProfile(scores, allProfiles);

  const isConfirmedDual = matchResult.isDualProfile && !!effectiveProfileParam;
  const otherProfile =
    isConfirmedDual && matchResult.secondary
      ? selectedProfile.id === matchResult.primary.id
        ? matchResult.secondary
        : matchResult.primary
      : null;

  function handleSaveProfileSelection(profileId: string) {
    setOptimisticProfileId(profileId);
    setInFlight((n) => n + 1);

    void (async () => {
      try {
        await saveProfileSelection({
          data: { reportId: data.id, profileId },
        });
        await navigate({
          to: "/report/$id",
          params: { id: data.id },
          search: { profile: undefined, force_dual: undefined },
        });
        await router.invalidate();
      } catch (err) {
        console.error("Profile selection failed:", err);
        await captureHandledException(err, {
          feature: "report",
          action: "select-profile",
          layer: "client",
          fallback: "toast",
          extra: { reportId: data.id, profileId, route: "report" },
        });
        toast.error(
          "Something went wrong saving your profile. Please try again.",
        );
        setOptimisticProfileId((cur) => (cur === profileId ? null : cur));
      } finally {
        setInFlight((n) => n - 1);
      }
    })();
  }

  return (
    <>
      {/* Screen view */}
      <div className="flex-1 print:hidden">
        <div className="max-w-[1180px] mx-auto">
          {isConfirmedDual && otherProfile ? (
            <>
              {/* Confirmed Dual View (Y16GC design) */}
              <section className="flex flex-col items-center gap-5 px-4 sm:px-[60px] py-8">
                <h2 className="text-4xl text-core-orange tracking-widest text-center">
                  {SECTION_LABELS.coreProfile}
                </h2>
                <p className="text-white/60 text-center">
                  You selected your profile
                </p>
              </section>

              {/* Tabs */}
              <ProfileTabs
                profiles={[matchResult.primary, matchResult.secondary!]}
                activeId={selectedProfile.id}
                onSelect={handleSaveProfileSelection}
              />

              {/* Confirmation Banner */}
              <div className="flex justify-center px-4 sm:px-[60px] my-3">
                <div className="flex items-center justify-center gap-3 px-8 py-3 rounded-xl bg-core-orange/10">
                  <span className="text-core-orange text-2xl">{"\u2705"}</span>
                  <span className="text-core-orange font-bold">
                    You identified as {selectedProfile.name}
                  </span>
                </div>
              </div>

              {/* Share Section */}
              <section className="flex items-center gap-4 px-4 sm:px-[60px] py-4">
                <div className="flex-1">
                  <ShareLink id={data.id} />
                </div>
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="shrink-0 px-6 py-3 rounded-lg border border-core-mid bg-core-gray/30 text-white text-sm font-bold hover:border-white/40 transition-colors"
                >
                  Save as PDF
                </button>
              </section>

              {/* Tagline */}
              <div className="flex items-center justify-center px-4 sm:px-[60px] py-4">
                <span className="text-core-orange text-sm text-center">
                  {selectedProfile.tagline}
                </span>
              </div>

              {/* Profile Name */}
              <div className="flex items-center justify-center px-4 sm:px-[60px] py-4">
                <h3 className="text-3xl text-white text-center">
                  {selectedProfile.name.toUpperCase()}
                </h3>
              </div>

              {/* Opening Quote */}
              <section className="px-4 sm:px-[60px] py-6">
                <RichText
                  value={selectedProfile.openingQuote}
                  className="text-white/70 italic leading-relaxed text-center"
                />
              </section>

              {/* The Model */}
              <section className="px-4 sm:px-[60px] py-10 sm:py-12">
                <div className="w-full h-px bg-white/10 mb-6" />
                <h3 className="text-2xl text-core-orange tracking-wider mb-6">
                  {SECTION_LABELS.keyModel}
                </h3>
                <KeyModelContent
                  content={selectedProfile.keyModel.content}
                  className="text-white/80 leading-relaxed"
                />
              </section>

              {/* Profile Content */}
              <div className="py-4 lg:py-6">
                <div className="flex flex-col gap-6 lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
                  <div className="min-w-0 lg:order-1 px-4 sm:pl-15">
                    <ProfileContent profile={selectedProfile} />
                    <ProfileInvitations profile={selectedProfile} />
                  </div>
                  <RelatedProfilesSidebar
                    variant="report"
                    activeProfile={selectedProfile}
                    profiles={allProfiles}
                    prioritizedProfileId={otherProfile.id}
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Standard Single Profile View (dwmCn design) */}
              <ProfileHeader profile={selectedProfile} reportId={data.id} />
              <div className="py-4 lg:py-6">
                <div className="flex flex-col gap-6 lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
                  <div className="min-w-0 lg:order-1 px-4 sm:pl-15">
                    <ProfileContent profile={selectedProfile} />
                    <ProfileInvitations profile={selectedProfile} />
                  </div>
                  <RelatedProfilesSidebar
                    variant="report"
                    activeProfile={selectedProfile}
                    profiles={allProfiles}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Sticky footer for switching profiles (dual-profile confirmed view, desktop only) */}
      {isConfirmedDual && otherProfile && (
        <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-white/10 bg-core-dark-gray/95 backdrop-blur-sm print:hidden hidden lg:block">
          <div className="max-w-[1080px] mx-auto flex items-center justify-center gap-3 px-4 sm:px-[60px] py-3">
            <span className="text-white/40 text-sm">
              Want to explore the other profile?
            </span>
            <button
              type="button"
              onClick={() => handleSaveProfileSelection(otherProfile.id)}
              className="text-core-orange text-sm font-bold hover:underline"
            >
              Switch to {otherProfile.name} {"\u2192"}
            </button>
          </div>
        </div>
      )}

      {/* Print view */}
      <PrintView profile={selectedProfile} />
    </>
  );
}
